import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { Button, Animation, Spinner } from 'components'
import { ConditionalInput } from 'features'
import { FaArrowDown, FaFileAlt } from 'react-icons/fa'
import { AnimateSharedLayout, motion } from 'framer-motion'
import SubmarkLogo from 'resources/Logo/submark-logo-2.png'
import { AnimatePresence } from 'framer-motion'
import { Header, Selector, AccountSelector, TagSelector, MultiSelector, TextInput } from '../../components'

import { 
    triggerContext, 
    clientContext, 
    titleContext, 
    notesContext,
    priorityOptions,
    pageContext,
    sectionContext,
    workflowContext
} from 'context'
import classes from './Inflow.module.css';

const Column = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 4;
    padding-bottom: 2rem;
`

const Row = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 0.5rem;
`

const SubmitSection = styled.div`
    justify-self: end;
`

const submitStyle = {
    backgroundColor: 'var(--focus-color)',
    width: 'min-content',
    fontWeight: 'bold',
    margin: '0.5rem 0'
}

const InflowScreen = props => {
    const {
        state,
        onSelect, // i.e. setState
        onNext,
        onSubmit, 
    } = props;
    const uid = useSelector((state) => state.auth.uid)
    const user = useSelector((state) => state.auth.user)
    const data = useSelector((state) => state.db.present.data && state.db.present.data.toJS())
    const [triggerOptions, setOptions] = React.useState({})
    const [spinner, setSpinner] = React.useState(false)

    const workflow = state['workflow']
    const trigger = state['trigger']
    const action = state['action']

    React.useEffect(() => {
        Object.keys(triggerOptions).length > 0 && setSpinner(false)
    }, [triggerOptions])

    React.useEffect(() => {
        const configurations = trigger['client'] && clientContext[trigger['client']]['configurations'];
        configurations && Object.values(configurations).map(config => {
            if(typeof config['pre_requisite'] === 'function' && config['pre_requisite']({old: state, new: state})){
                getTriggerOptions(state, state)
                setSpinner(true)
            }
        })
        setTimeout(() => {
            setSpinner(false)
        }, 1000)
    }, [])

    ///////////////
    // UTILITIES //
    ///////////////
    const getPages = () => {
        const pages = data['pages']
        const timezone = user['timezone']

        let pageOptions = {};
        for (const [pageID, page] of Object.entries(pages)) {
            if(page['status'] === pageContext.STATUS.ACTIVE){
                pageOptions[pageID] = {
                    title: page['page_name'],
                    // subtitle: `Created ${moment(page['create_date']).tz(timezone).format('MMM D Y')}`,
                    icon: page['icon'] || { type: 'element', value: <FaFileAlt/> },
                    value: pageID
                }
            }
        }
        return pageOptions
    }

    const getSections = () => {
        const timezone = user['timezone']
        const page = data['pages'][action.ext['pageID']]
        const sectionIDs = page && page['sections']

        let sectionOptions = {}
        page && sectionIDs.map(sectionID => {
            const section = data['sections'][sectionID]
            if(section && section['status'] === sectionContext.STATUS.ACTIVE) {
                sectionOptions[sectionID] = {
                    title: section['section_name'],
                    // subtitle: `Created ${moment(section['create_date']).tz(timezone).format('MMM D Y')}`,
                    value: sectionID
                }
            }
        })
        return sectionOptions
    }

    const getTitle = (value) => {
        const oldTitle = workflow['workflowTitle'] 
        const triggerSelected = value || trigger['trigger']
        const appSelected = trigger['client']
        if(!appSelected){
            return 'Untitled'
        } else if (appSelected && !triggerSelected) {
            return 'Untitled'
        } else if (appSelected && triggerSelected) {
            if(oldTitle === 'Untitled'){
                return `${clientContext[appSelected]['title']} - ${triggerContext[appSelected][triggerSelected]['title']}`
            } else {
                return oldTitle
            }
        }
    }

    const onSelectAccountHandler = (value) => {
        // Update Editor state
        const oldState = {...state}
        let updatedState = {...state, clientId: value, trigger: {...trigger, clientId: value['clientId'], clientTitle: value['clientTitle']}, error: { trigger: { ext: {} }, action: { exit: {} } }}
        console.log(updatedState)
        onSelect(updatedState)
        setOptions({}) // Included this to remove the need to reset options in onSelectTriggerHandler

        // Get options for additional selectors (if any)
        const configurations = clientContext[trigger['client']]['configurations'];
        configurations && Object.values(configurations).map(config => {
            if(typeof config['pre_requisite'] === 'function'){
                const validation = config['pre_requisite']({new: updatedState, old: oldState})
                if(validation['shouldUpdate'] && validation['isValid']){
                    getTriggerOptions(updatedState, oldState)
                    setSpinner(true)
                }
            }
        })
    }

    const onSelectTriggerHandler = (value) => {
        // Update Editor state
        const oldState = {...state}
        let updatedState = {...state, error: { trigger: { ext: {} }, action: { ext: {} } }, 
                            trigger: {...trigger, trigger: value}, 
                            workflow: {...workflow, workflowTitle: getTitle(value)} }
        onSelect(updatedState)

        // Get options for additional selectors (if any)
        const configurations = clientContext[trigger['client']]['configurations'];
        for (const config of Object.values(configurations)) {
            if(typeof config['pre_requisite'] === 'function'){
                const validation = config['pre_requisite']({new: updatedState, old: oldState})
                if(validation['shouldUpdate']){
                    setSpinner(true)
                    // setOptions({}) // Why need this ar? (Replace at onSelectAccountHandler)
                    setTimeout(() => {getTriggerOptions(updatedState, oldState)}, 500)
                } else if (!validation['isValid']){
                    console.log(triggerOptions, config.key, configurations)
                    if(Object.keys(triggerOptions).includes(config['value'])){
                        let updatedOptions = {...triggerOptions}
                        delete updatedOptions[config['value']]
                        setOptions(updatedOptions)
                    }
                } 
            }
        }
    }

    const onSelectHandler = (value, key, refreshOptions) => {
        // Update Editor state
        const oldState = {...state}
        const updatedState = { 
            ...state, 
            trigger: {...state.trigger, ext: {...state.trigger.ext, [key]: value}}, 
            error: { trigger: { ext: {} }, action: { ext: {} } } 
        }
        onSelect(updatedState)

        // Get options for additional selectors (if any)
        const configurations = clientContext[trigger['client']]['configurations'];
        if(refreshOptions){
            for (const config of Object.values(configurations)) {
                if(typeof config['pre_requisite'] === 'function'){
                    const validation = config['pre_requisite']({new: updatedState, old: oldState})
                    if(validation['shouldUpdate']){
                        setSpinner(true)
                        // setOptions({}) // Why need this ar? (Replace at onSelectAccountHandler)
                        setTimeout(() => {getTriggerOptions(updatedState, oldState)}, 500)
                    } else if (!validation['isValid']){
                        console.log('[Remove options]', config)
                        if(Object.keys(triggerOptions).includes(config['key'])){
                            let updatedOptions = {...triggerOptions}
                            delete updatedOptions[config['key']]
                            setOptions(updatedOptions)
                        }
                    }
                } 
            }
        }
        

    }

    const refreshOptions = (key) => {
        const handler = clientContext[state.trigger['client']].configurations[key]['options']
        if(handler && typeof handler === 'function'){
            clientContext[state.trigger['client']].configurations[key]['options']({...state, userId: uid})
            .then(res => {
                console.log('[refreshOptions] Completed', res)
                let updatedOptions = {...triggerOptions}
                const configurations = clientContext[trigger['client']]['configurations'];
                updatedOptions[key] = {label: configurations[key]['label'], options: res['options']}
                setOptions(updatedOptions)
            })
            .catch(err => {
                console.log('[refreshOptions] Failed', err)
            })
        }
        
    }

    const getExtTriggers = () => {
        if(!trigger['client']){ return }
        const configurations = clientContext[trigger['client']]['configurations'];
        let components = [];
        // console.log('[Additional components]', {config: configurations, options: triggerOptions})
        if(!configurations){ return }

        for (const [key, trigger] of Object.entries(configurations)) {
            // Create components
            if(trigger['selector'] === 'Selector') {
                triggerOptions[key] && components.push(
                <Selector label={triggerOptions[key]['label']} key={triggerOptions[key]['value']}
                    value={state['trigger'].ext && state['trigger'].ext[key]} 
                    helper={trigger['helper']}
                    options={triggerOptions[key]['options']} 
                    onSelect={(value) => onSelectHandler(value, key, true)}
                    error={state.error.trigger.ext[key]}
                    refreshOptions={() => getTriggerOptions(state, state)}/>)
            } else if (trigger['selector'] === 'MultiSelector') {
                triggerOptions[key] && components.push(
                    <MultiSelector label={triggerOptions[key]['label']} 
                        selected={state['trigger'].ext && state['trigger'].ext[key] || []} 
                        options={triggerOptions[key]['options']} 
                        helper={trigger['helper']}
                        onSelect={(value) => onSelectHandler(value, key, false)}/>
                )
            } else if (trigger['selector'] === 'Text') {
                triggerOptions[key] && components.push(
                    <TextInput label={triggerOptions[key]['label']} 
                        value={state['trigger'].ext && state['trigger'].ext[key]} 
                        error={state.error.trigger.ext[key]}
                        helper={trigger['helper']}
                        onChange={(value) => onSelectHandler(value, key, true)}/>

                )
            } else if (trigger['selector'] === 'Conditional') {
                triggerOptions[key] && components.push(
                    <ConditionalInput label={triggerOptions[key]['label']} placeholder='Please input a valid Gsheet URL and range ID'
                        value={state['trigger'].ext && state['trigger'].ext[key] || []} 
                        error={state.error.trigger.ext[key]}
                        errors={[]}
                        options={triggerOptions[key]['options']} 
                        refreshOptions={() => refreshOptions(key)}
                        helper={trigger['helper']}
                        onEdit={(value) => onSelectHandler(value, key, false)}
                        />
                )
            }
        }

        return components
    }

    const getTriggerOptions = async (updatedState, oldState) => {
        const configurations = clientContext[updatedState.trigger['client']]['configurations'];
        let options = {};

        if(!configurations) { return }
        for (const [key, trigger] of Object.entries(configurations)) {
            // Check for pre-requisite are fufilled
            if(typeof trigger['pre_requisite'] === 'function'){ 
                const validation = trigger['pre_requisite']({new: updatedState, old: oldState})
                if(validation['isValid']){
                    // console.log('[Get options]', trigger)
                    // Get options            
                    if(trigger['options'] && typeof trigger['options'] == 'function'){
                        await trigger['options']({...updatedState, userId: uid})
                        .then(option => {
                            // console.log(trigger['selector'], option, trigger)
                            // Create component
                            if(trigger['selector'] === 'Selector') {
                                options[key] = {label: trigger['label'], options: option['options']}
                            } else if (trigger['selector'] === 'MultiSelector') {
                                options[key] = {label: trigger['label'], options: option['options']}
                            } else if (trigger['selector'] === 'Conditional') {
                                // console.log('[Conditional options]')
                                options[key] = {label: trigger['label'], options: option['options']}
                            }
                            
                        })
                        .catch(err => {
                            console.log('Cannot get options', err)
                        }) 
                    } else if (['Text'].includes(trigger['selector'])){
                        options[key] = { label: trigger['label'] }
                    }
                }
                
            } 
        }
        // console.log('[getTriggerOptions]', options)

        Object.keys(options).length > 0 && setOptions(options)
    }

    ////////////////
    // COMPONENTS //
    ////////////////
    // Main triggers
    const triggerSection =
    <div className={state.section === 'TRIGGER' ? classes.TriggerOpen : classes.TriggerClosed} 
        onClick={() => state.section !== 'TRIGGER' && onSelect({ section: 'TRIGGER', error: { trigger: { ext: {} }, action: { ext: {} } } })}>
        <Header title='Select app' subtitle='Trigger'/>
        <AnimatePresence initial={false}>
            {state.section === 'TRIGGER' && 
            <Animation animation='expand'>
                <AnimateSharedLayout>
                    <Column>
                        <motion.div layout>
                            <Selector label='Select app'
                                value={trigger['client']} 
                                options={_.pick(clientContext, workflowContext.INFLOW)} 
                                onSelect={(value) => {
                                    onSelect({ ...state, trigger: {client: value, trigger: null, clientId: null, ext: {}}, workflow: {...workflow, workflowTitle: 'Untitled'},error: { trigger: { ext: {} }, action: { ext: {} } } })
                                    setOptions({})
                                }} 
                                error={state.error.trigger['client']}/>
                        </motion.div>

                        <motion.div layout>
                        {trigger['client'] &&
                        <AccountSelector value={trigger['clientId']} selectedClient={trigger['client']}
                            onSelect={onSelectAccountHandler} 
                            onError={(value) => onSelect({...state, error: {...state.error, clientId: value}})}
                            error={state.error.trigger['clientId']} />}
                        </motion.div>

                        <motion.div layout>

                        </motion.div>

                        <motion.div layout>
                        {trigger['client'] &&
                        <Selector label='Select trigger' 
                            value={trigger['trigger']} 
                            options={triggerContext[trigger['client']]} 
                            onSelect={onSelectTriggerHandler} 
                            error={state.error.trigger['trigger']}/>}
                        </motion.div>

                        <motion.div layout>
                            {/* Additional selector */}
                            {getExtTriggers()}
                        </motion.div>

                        {/* Spinner */}
                        {spinner &&
                        <motion.div layout style={{display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0.5rem 0'}}>
                            <Spinner text='Checking for additional options'
                                styles={{color: 'var(--focus-color)', flexDirection: 'row'}}/>
                        </motion.div>}
                            
                    </Column>
                </AnimateSharedLayout>
            </Animation>}
        </AnimatePresence>

        {state.section === 'TRIGGER' && 
        <SubmitSection>
            <Button text={state.spinner === 'NEXT' ? <Spinner styles={{color: 'var(--text-color)'}}/> : 'Next'} 
                styles={submitStyle} onSelect={onNext}/>
        </SubmitSection>}
    </div>

    const actionSection =
    <div className={state.section === 'ACTION' ? classes.ActionOpen : classes.ActionClosed} >
        <Header title='Atomicly' subtitle='Action - Create item' 
            iconBackgroundColor='var(--focus-color)'
            icon={<img src={SubmarkLogo} style={{width:'auto', height:'1.3rem',objectFit:'contain', marginRight:'0.1rem'}}/>}/>

        <AnimatePresence>
            {state.section === 'ACTION' &&
            <Animation animation='expand'>
                <Column>
                    <Selector label='Title' 
                        value={action.ext['title']} 
                        options={titleContext[trigger['client']] || clientContext[trigger['client']]['titleOptions'](state.workflow)} 
                        onSelect={(value) => onSelect({ ...state, action: {...action, ext: { ...action.ext, title: value}}, error: { trigger: { ext: {} }, action: { ext: {} } } })} 
                        error={state.error.action.ext['title']}/>
                    
                    <MultiSelector label='Notes' 
                        selected={action.ext['notes']} 
                        options={notesContext[trigger['client']] || clientContext[trigger['client']]['notesOptions'](state.workflow)} 
                        onSelect={(value) => onSelect({ ...state, action: {...action, ext: { ...action.ext, notes: value}}, error: { trigger: { ext: {} }, action: { ext: {} } } })} />

                    <Row>
                        <Selector label='Page' 
                            value={action.ext['pageID']} 
                            options={getPages()} 
                            onSelect={(value) => onSelect({ ...state, action: {...action, ext: { ...action.ext, pageID: value}}, error: { trigger: { ext: {} }, action: { ext: {} } } })} 
                            error={state.error.action.ext['pageID']}/>
                        
                        <Selector label='Section' 
                            value={action.ext['sectionID']} 
                            options={getSections()} 
                            onSelect={(value) => onSelect({ ...state, action: {...action, ext: { ...action.ext, sectionID: value}},  error: { trigger: { ext: {} }, action: { ext: {} } } })} 
                            error={state.error.action.ext['sectionID']} optionPlaceholder='Please select a page first'/>
                    </Row>

                    <Row>
                        <Selector label='Priority' 
                            value={action.ext['priority']} 
                            options={priorityOptions} 
                            onSelect={(value) => onSelect({ ...state, action: {...action, ext: { ...action.ext, priority: value}}, error: { trigger: { ext: {} }, action: { ext: {} } } })}/>

                        <TagSelector pageID={action.ext['pageID']} selected={action.ext['tags']}
                            onSelect={(value) => onSelect({ ...state, action: {...action, ext: { ...action.ext, tags: value}},error: { trigger: { ext: {} }, action: { ext: {} } } })}/>
                    </Row>
                </Column>
            </Animation>}
        </AnimatePresence>

        {state.section === 'ACTION' && 
        <SubmitSection>
            <Button text={state.spinner === 'SUBMIT' ? <Spinner styles={{color:'var(--text-color)'}}/> : 'Save'}
                styles={submitStyle} onSelect={onSubmit}/>
        </SubmitSection>}
    </div>

    const arrow = <FaArrowDown style={{fontSize: '2rem', margin: '1rem', color: 'var(--primary-accent-color)'}}/>

    // console.log('[Inflow.js] extTrigger',extTrigger)
    return (
        <AnimateSharedLayout type="crossfade">
            <motion.div >{triggerSection}</motion.div>
            <motion.div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>{arrow}</motion.div>
            <motion.div >{actionSection}</motion.div>
        </AnimateSharedLayout>
    )
}

export default InflowScreen